import React, { useCallback, useEffect, useState } from 'react';
import ContactsContext from './Contacts.context';
import useSelect from 'hooks/useSelect';
import { Contact } from 'models/Contact';
import { useTranslation } from 'react-i18next';
import confirm from 'modules/confirm';
import api from 'api';
import { ContactsManageDrawer } from '../../pages/ContactsList/components/ContactsManageDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { ManageDrawerRefProps } from '../../pages/ContactsList/components/ContactsManageDrawer/ContactsManageDrawer.component';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

type ContactsProps = {
  contacts: Contact[];
  refetch?: () => void;
};

const Contacts: React.FC<ContactsProps> = (props) => {
  const { children, contacts, refetch } = props;

  const {
    selected,
    isAllSelected,
    selectAll,
    removeAll,
    updateSelected,
    updateSelectedValue,
  } = useSelect<Contact>(contacts);

  const [isDeleting, setIsDeleting] = useState(false);
  const [manageDrawer, manageDrawerRef] =
    useCallbackRef<ManageDrawerRefProps>(null);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const handleManage = useCallback(
    (selected: Contact[]) => {
      manageDrawer?.setOpen(true);
      manageDrawer?.setContacts(selected);
    },
    [manageDrawer],
  );

  const handleDelete = useCallback(
    async (selected: Contact[]) => {
      try {
        if (
          await confirm(
            {
              title: t('areYouSureDeleteContacts', { count: selected.length }),
              content: t('deleteWarning'),
              type: 'delete',
            },
            true,
          )
        ) {
          setIsDeleting(true);
          await api.contacts.bulkDeleteContacts(selected.map(({ _id }) => _id));
          removeAll();
          refetch();
        }
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [refetch, removeAll, t, getMixpanelProps],
  );

  useEffect(() => {
    removeAll();
  }, [contacts, removeAll]);

  return (
    <ContactsContext.Provider
      value={{
        selected,
        isAllSelected,
        isDeleting,
        selectAll,
        handleManage,
        removeAll,
        handleDelete,
        updateSelected,
        updateSelectedValue,
      }}
    >
      {children}

      <ContactsManageDrawer ref={manageDrawerRef} />
    </ContactsContext.Provider>
  );
};

export default Contacts;
